// Customer-portal upload pipeline (ADR-007 rev — content-addressed,
// portal-mediated).
//
// The browser POSTs the raw file bytes to the portal API's /storage/upload
// endpoint; the API hashes them, derives the content-addressed key
// (`<tenant_id>/files/<sha256>`), writes the blob ONCE via managed identity
// (dedupe on repeat content), and records the Asset row — returning the
// AssetDto in one round-trip. This REPLACES the old three-step browser-direct
// SAS dance (request signed URL → PUT to provider → register): content-
// addressing needs the sha256 before the write, and write-SAS is banned
// (no account keys on the portal side).
//
// The list / delete / preview helpers round out the surface for rendering
// thumbnails of previously-uploaded files.

import { normalizeUploadContentType } from 'shared';
import type { AssetDto, BarcodeScan, ComponentRole } from 'shared';

import { api } from './client.js';

export async function listAssets(quote_id: string): Promise<AssetDto[]> {
  const res = await api.get<{ assets: AssetDto[] }>('/storage/assets', {
    params: { quote_id },
  });
  return res.data.assets;
}

// Detach a file from its slot. `retained_elsewhere` comes back true when the
// same file is still attached to other work of the caller's — the file stays on
// file, this slot just stops pointing at it. The SPA says so rather than letting
// "Remove" read as "delete everywhere".
export async function deleteAsset(asset_id: string): Promise<{ retained_elsewhere: boolean }> {
  const res = await api.delete<{ retained_elsewhere?: boolean }>(`/storage/assets/${asset_id}`);
  return { retained_elsewhere: res.data?.retained_elsewhere === true };
}

// Fetch the preview bytes through the portal's same-origin proxy. Used
// for thumbnail rendering — bypasses the cross-origin <img src=SAS>
// fragility (ad-blockers, third-party-cookie phaseout, Azure CORS). The
// proxy serves images directly and serves PDF source bytes (the SPA
// renders a first-page thumbnail from those client-side).
export async function fetchAssetPreviewBlob(asset_id: string): Promise<Blob> {
  const res = await api.get<Blob>(`/storage/assets/${asset_id}/preview`, {
    responseType: 'blob',
  });
  return res.data;
}

// Object-URL convenience over fetchAssetPreviewBlob for the image path
// (the bytes go straight into an <img src>). The caller is responsible
// for revoking the returned object URL.
export async function fetchAssetPreviewBlobUrl(asset_id: string): Promise<string> {
  return URL.createObjectURL(await fetchAssetPreviewBlob(asset_id));
}

// Upload a file and return the resulting AssetDto. The bytes go browser →
// portal API (not browser → provider) so the API can content-address + dedupe.
//
// `barcode_scan_promise` is an OPTIONAL, already-running barcode-presence scan
// (see components/detectBarcode.ts). The caller kicks it off the moment the file
// is picked so it runs IN PARALLEL with the upload; we await it here only just
// before sending so its result rides on the same request. Detection never blocks
// or fails the upload — a slow/missing/rejected scan just uploads with no result
// (the row stays null), and the scan helper itself never throws.
export async function uploadFile(args: {
  file: File;
  quote_id: string;
  quote_line_id?: string | null;
  component_role?: ComponentRole;
  // Which build step of the kit a reference photo illustrates (ADR-018 phase 4).
  // Only accepted alongside component_role 'assembly_step_photo' — the route
  // refuses it on any other role, where nothing would ever read it back.
  assembly_step_id?: string | null;
  onProgress?: (fraction: number) => void;
  barcode_scan_promise?: Promise<BarcodeScan>;
}): Promise<AssetDto> {
  const role: ComponentRole = args.component_role ?? 'general';
  // Prefer the browser-reported MIME, fall back to an extension lookup (browsers
  // report nothing for .psd/.indd/.ai). This is the content_type we transmit +
  // persist; the request body itself is sent as opaque octet-stream.
  const content_type = normalizeUploadContentType(args.file.name, args.file.type);

  // Resolve the (parallel) barcode scan before sending so it rides on the same
  // request. Defensive catch even though detectBarcode never throws — a barcode
  // advisory must never be the reason an upload fails.
  const barcode_scan = args.barcode_scan_promise
    ? await args.barcode_scan_promise.catch(() => undefined)
    : undefined;

  const res = await api.post<{ asset: AssetDto }>('/storage/upload', args.file, {
    params: {
      quote_id: args.quote_id,
      filename: args.file.name,
      content_type,
      component_role: role,
      ...(args.quote_line_id ? { quote_line_id: args.quote_line_id } : {}),
      ...(args.assembly_step_id ? { assembly_step_id: args.assembly_step_id } : {}),
      ...(barcode_scan !== undefined ? { barcode_scan } : {}),
    },
    // Send the body as raw bytes — the backend route buffers it with
    // express.raw and reads all metadata from the query params above.
    headers: { 'Content-Type': 'application/octet-stream' },
    ...(args.onProgress
      ? {
          onUploadProgress: (e: { loaded: number; total?: number }) => {
            if (e.total) args.onProgress!(e.loaded / e.total);
          },
        }
      : {}),
  });
  return res.data.asset;
}
